import { useState } from 'react';
import {
  createFrontendPlugin,
  PageBlueprint,
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/frontend-plugin-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  InfoCard,
  WarningPanel,
  Header,
  Page,
  Content,
  Link,
} from '@backstage/core-components';
import {
  TextField,
  MenuItem,
  Button,
  Grid,
  IconButton,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Divider,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import EmailIcon from '@material-ui/icons/Email';
import AddIcon from '@material-ui/icons/Add';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import PeopleIcon from '@material-ui/icons/People';
import PersonIcon from '@material-ui/icons/Person';
import EventIcon from '@material-ui/icons/Event';
import AlternateEmailIcon from '@material-ui/icons/AlternateEmail';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SearchIcon from '@material-ui/icons/Search';
import { gmailDelegationPermission } from './conduitPermissions';

// --- Shared helpers ---

const AccessDeniedPanel = () => (
  <WarningPanel title="Access Denied" severity="error">
    This function requires the <strong>Help Desk Agent</strong> or{' '}
    <strong>Platform Admin</strong> role.
  </WarningPanel>
);

const BackToGam = () => (
  <Box mb={2}>
    <Link to="/gam">
      <Button startIcon={<ArrowBackIcon />} size="small">
        Back to GAM
      </Button>
    </Link>
  </Box>
);

function LookupForm({
  label,
  helperText,
  value,
  onChange,
  onSubmit,
  loading,
}: {
  label: string;
  helperText: string;
  value: string;
  onChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
}) {
  return (
    <InfoCard title="Lookup">
      <form onSubmit={onSubmit}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <TextField
              label={label}
              helperText={helperText}
              value={value}
              onChange={e => onChange(e.target.value)}
              required
              fullWidth
              type="email"
              placeholder="john.smith@younglife.org"
            />
          </Grid>
          <Grid item>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : <SearchIcon />}
            >
              {loading ? 'Looking up...' : 'Look Up'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </InfoCard>
  );
}

// --- GAM Hub ---

function GamHub() {
  const tools = [
    {
      title: 'Manage Gmail Delegates',
      description: 'Add or remove users who can send email on behalf of a Young Life mailbox.',
      icon: <EmailIcon fontSize="large" color="action" />,
      path: '/gam/manage-delegates',
    },
    {
      title: 'View Gmail Delegates',
      description: 'See who currently has delegation access to a Young Life mailbox.',
      icon: <PeopleIcon fontSize="large" color="action" />,
      path: '/gam/view-delegates',
    },
    {
      title: 'Manage Aliases',
      description: 'Add or remove email aliases (alternate addresses) for a user.',
      icon: <AlternateEmailIcon fontSize="large" color="action" />,
      path: '/gam/manage-aliases',
    },
    {
      title: 'Calendar Delegation',
      description: 'Share calendar access so another user can view or manage someone\'s calendar.',
      icon: <EventIcon fontSize="large" color="action" />,
      path: '/gam/calendar-delegation',
    },
    {
      title: 'User Profile',
      description: 'Look up account details: status, org unit, last login, and all email addresses.',
      icon: <PersonIcon fontSize="large" color="action" />,
      path: '/gam/user-profile',
    },
  ];

  return (
    <Page themeId="tool">
      <Header
        title="Google Admin Management"
        subtitle="Workspace tools for Young Life administrators"
      />
      <Content>
        <RequirePermission
          permission={gmailDelegationPermission}
          errorPage={<AccessDeniedPanel />}
        >
          <Grid container spacing={3}>
            {tools.map(tool => (
              <Grid item xs={12} sm={6} md={4} key={tool.title}>
                <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent style={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" mb={1} style={{ gap: 8 }}>
                      {tool.icon}
                      <Typography variant="h6">{tool.title}</Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {tool.description}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Link to={tool.path}>
                      <Button size="small" color="primary" variant="contained">
                        Open
                      </Button>
                    </Link>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </RequirePermission>
      </Content>
    </Page>
  );
}

// --- Manage Gmail Delegates ---

type DelegateResult = { email: string; status: 'success' | 'error'; message: string };

function ManageDelegatesPage() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [delegatorEmail, setDelegatorEmail] = useState('');
  const [delegateEmails, setDelegateEmails] = useState(['']);
  const [action, setAction] = useState('add');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<DelegateResult[]>([]);

  const addDelegate = () => setDelegateEmails(prev => [...prev, '']);
  const removeDelegate = (i: number) =>
    setDelegateEmails(prev => prev.filter((_, idx) => idx !== i));
  const updateDelegate = (i: number, value: string) =>
    setDelegateEmails(prev => prev.map((e, idx) => (idx === i ? value : e)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setResults([]);
    const baseUrl = await discoveryApi.getBaseUrl('conduit-gmail-delegation');
    const emails = delegateEmails.filter(e => e.trim());
    const settled = await Promise.allSettled(
      emails.map(delegate_email =>
        fetchApi
          .fetch(`${baseUrl}/delegate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ delegator_email: delegatorEmail, delegate_email, action }),
          })
          .then(async res => {
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
            return data;
          }),
      ),
    );
    setResults(
      settled.map((r, i) => ({
        email: emails[i],
        status: r.status === 'fulfilled' ? 'success' : 'error',
        message:
          r.status === 'fulfilled'
            ? r.value.message
            : (r as PromiseRejectedResult).reason?.message ?? 'Unknown error',
      })),
    );
    setSubmitting(false);
  };

  return (
    <Page themeId="tool">
      <Header title="Manage Gmail Delegates" subtitle="Add or remove email delegation for Young Life mailboxes" />
      <Content>
        <RequirePermission permission={gmailDelegationPermission} errorPage={<AccessDeniedPanel />}>
          <BackToGam />
          <InfoCard title="Delegation Parameters">
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Mailbox Owner"
                    helperText="The mailbox to modify"
                    value={delegatorEmail}
                    onChange={e => setDelegatorEmail(e.target.value)}
                    required fullWidth type="email"
                    placeholder="john.smith@younglife.org"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Action" select value={action}
                    onChange={e => setAction(e.target.value)}
                    required fullWidth
                  >
                    <MenuItem value="add">Add Delegation</MenuItem>
                    <MenuItem value="remove">Remove Delegation</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Delegates</Typography>
                  <Grid container spacing={2}>
                    {delegateEmails.map((email, i) => (
                      <Grid item xs={12} md={6} key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <TextField
                          label={`Delegate ${delegateEmails.length > 1 ? i + 1 : ''}`}
                          helperText="Person gaining or losing access"
                          value={email} onChange={e => updateDelegate(i, e.target.value)}
                          required fullWidth type="email" placeholder="jane.doe@younglife.org"
                        />
                        {delegateEmails.length > 1 && (
                          <IconButton onClick={() => removeDelegate(i)} size="small" aria-label="remove">
                            <RemoveCircleOutlineIcon />
                          </IconButton>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                  <Button startIcon={<AddIcon />} onClick={addDelegate} size="small" style={{ marginTop: 8 }}>
                    Add another delegate
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" color="primary" disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={18} /> : undefined}>
                    {submitting ? 'Processing...' : action === 'add'
                      ? `Add ${delegateEmails.filter(e => e.trim()).length > 1 ? 'Delegates' : 'Delegation'}`
                      : `Remove ${delegateEmails.filter(e => e.trim()).length > 1 ? 'Delegates' : 'Delegation'}`}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </InfoCard>
          {results.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <InfoCard title="Results">
                <Grid container spacing={1}>
                  {results.map((r, i) => (
                    <Grid item xs={12} key={i}>
                      <Alert severity={r.status === 'success' ? 'success' : 'error'}>
                        <strong>{r.email}</strong> — {r.message}
                      </Alert>
                    </Grid>
                  ))}
                </Grid>
              </InfoCard>
            </div>
          )}
        </RequirePermission>
      </Content>
    </Page>
  );
}

// --- View Gmail Delegates ---

type GmailDelegate = { delegateEmail: string; verificationStatus: string };

function ViewDelegatesPage() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [mailbox, setMailbox] = useState('');
  const [loading, setLoading] = useState(false);
  const [delegates, setDelegates] = useState<GmailDelegate[] | null>(null);
  const [lookedUp, setLookedUp] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setDelegates(null);
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('conduit-gmail-delegation');
      const res = await fetchApi.fetch(`${baseUrl}/delegates?delegator_email=${encodeURIComponent(mailbox)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setDelegates(data.delegates ?? []);
      setLookedUp(mailbox);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header title="View Gmail Delegates" subtitle="Look up current email delegates for a Young Life mailbox" />
      <Content>
        <RequirePermission permission={gmailDelegationPermission} errorPage={<AccessDeniedPanel />}>
          <BackToGam />
          <LookupForm label="Mailbox Owner" helperText="Look up who can access this mailbox"
            value={mailbox} onChange={setMailbox} onSubmit={handleLookup} loading={loading} />
          {error && <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>}
          {delegates !== null && (
            <div style={{ marginTop: 16 }}>
              <InfoCard title={`Delegates for ${lookedUp}`}
                subheader={delegates.length === 0 ? 'No delegates configured' : `${delegates.length} delegate${delegates.length !== 1 ? 's' : ''}`}>
                {delegates.length === 0
                  ? <Typography color="textSecondary">This mailbox has no delegates configured.</Typography>
                  : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Delegate Email</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {delegates.map(d => (
                          <TableRow key={d.delegateEmail}>
                            <TableCell>{d.delegateEmail}</TableCell>
                            <TableCell>
                              <Chip label={d.verificationStatus}
                                color={d.verificationStatus === 'accepted' ? 'primary' : 'default'}
                                size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
              </InfoCard>
            </div>
          )}
        </RequirePermission>
      </Content>
    </Page>
  );
}

// --- Manage Email Aliases ---

type AliasResult = { alias: string; status: 'success' | 'error'; message: string };
type AliasEntry = { aliasValue: string; primaryEmail: string };

function ManageAliasesPage() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [userEmail, setUserEmail] = useState('');
  const [lookupEmail, setLookupEmail] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [aliases, setAliases] = useState<AliasEntry[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [newAlias, setNewAlias] = useState('');
  const [aliasAction, setAliasAction] = useState('alias_add');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<AliasResult[]>([]);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingList(true);
    setAliases(null);
    setListError(null);
    setResults([]);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('conduit-gmail-delegation');
      const res = await fetchApi.fetch(`${baseUrl}/aliases?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setAliases(
        (data.aliases ?? []).map((a: any) => ({
          aliasValue: a.alias ?? a,
          primaryEmail: a.primaryEmail ?? userEmail,
        })),
      );
      setLookupEmail(userEmail);
    } catch (err: any) {
      setListError(err.message);
    } finally {
      setLoadingList(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setResults([]);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('conduit-gmail-delegation');
      const res = await fetchApi.fetch(`${baseUrl}/alias`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: lookupEmail || userEmail, alias: newAlias, action: aliasAction }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setResults([{ alias: newAlias, status: 'success', message: data.message }]);
      setNewAlias('');
      // Refresh list
      if (lookupEmail || userEmail) {
        const email = lookupEmail || userEmail;
        const baseUrl2 = await discoveryApi.getBaseUrl('conduit-gmail-delegation');
        const res2 = await fetchApi.fetch(`${baseUrl2}/aliases?user_email=${encodeURIComponent(email)}`);
        const data2 = await res2.json();
        if (res2.ok) {
          setAliases((data2.aliases ?? []).map((a: any) => ({ aliasValue: a.alias ?? a, primaryEmail: a.primaryEmail ?? email })));
        }
      }
    } catch (err: any) {
      setResults([{ alias: newAlias, status: 'error', message: err.message }]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header title="Manage Email Aliases" subtitle="Add or remove alternate email addresses for a user" />
      <Content>
        <RequirePermission permission={gmailDelegationPermission} errorPage={<AccessDeniedPanel />}>
          <BackToGam />
          <LookupForm label="User Email" helperText="Look up current aliases for this user"
            value={userEmail} onChange={setUserEmail} onSubmit={handleLookup} loading={loadingList} />

          {listError && <Alert severity="error" style={{ marginTop: 16 }}>{listError}</Alert>}

          {aliases !== null && (
            <div style={{ marginTop: 16 }}>
              <InfoCard title={`Aliases for ${lookupEmail}`}
                subheader={aliases.length === 0 ? 'No aliases configured' : `${aliases.length} alias${aliases.length !== 1 ? 'es' : ''}`}>
                {aliases.length === 0
                  ? <Typography color="textSecondary">This user has no email aliases.</Typography>
                  : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Alias Email</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {aliases.map(a => (
                          <TableRow key={a.aliasValue}>
                            <TableCell>{a.aliasValue}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}

                <Divider style={{ margin: '16px 0' }} />
                <Typography variant="subtitle2" gutterBottom>Modify Aliases</Typography>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} md={5}>
                      <TextField
                        label="Alias Email" helperText="The alias to add or remove"
                        value={newAlias} onChange={e => setNewAlias(e.target.value)}
                        required fullWidth type="email"
                        placeholder="j.smith@younglife.org"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Action" select value={aliasAction}
                        onChange={e => setAliasAction(e.target.value)} fullWidth
                      >
                        <MenuItem value="alias_add">Add Alias</MenuItem>
                        <MenuItem value="alias_remove">Remove Alias</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item>
                      <Button type="submit" variant="contained" color="primary" disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={18} /> : undefined}>
                        {submitting ? 'Saving...' : aliasAction === 'alias_add' ? 'Add Alias' : 'Remove Alias'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>

                {results.length > 0 && (
                  <Box mt={2}>
                    {results.map((r, i) => (
                      <Alert key={i} severity={r.status === 'success' ? 'success' : 'error'}>
                        {r.message}
                      </Alert>
                    ))}
                  </Box>
                )}
              </InfoCard>
            </div>
          )}
        </RequirePermission>
      </Content>
    </Page>
  );
}

// --- Calendar Delegation ---

type CalendarAcl = { delegate_email: string; role: string; id?: string };
type CalendarResult = { email: string; status: 'success' | 'error'; message: string };

const CALENDAR_ROLES = [
  { value: 'reader', label: 'Reader — can see event details' },
  { value: 'writer', label: 'Writer — can create and edit events' },
  { value: 'freeBusyReader', label: 'Free/Busy Reader — can see busy/free only' },
];

function CalendarDelegationPage() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [userEmail, setUserEmail] = useState('');
  const [lookupEmail, setLookupEmail] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [acl, setAcl] = useState<CalendarAcl[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [delegateEmail, setDelegateEmail] = useState('');
  const [role, setRole] = useState('writer');
  const [calAction, setCalAction] = useState('calendar_add');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<CalendarResult[]>([]);

  const refreshAcl = async (email: string) => {
    const baseUrl = await discoveryApi.getBaseUrl('conduit-gmail-delegation');
    const res = await fetchApi.fetch(`${baseUrl}/calendar-acl?user_email=${encodeURIComponent(email)}`);
    const data = await res.json();
    if (res.ok) setAcl(data.acl ?? []);
  };

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingList(true);
    setAcl(null);
    setListError(null);
    setResults([]);
    try {
      await refreshAcl(userEmail);
      setLookupEmail(userEmail);
    } catch (err: any) {
      setListError(err.message);
    } finally {
      setLoadingList(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setResults([]);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('conduit-gmail-delegation');
      const res = await fetchApi.fetch(`${baseUrl}/calendar-acl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: lookupEmail, delegate_email: delegateEmail, role, action: calAction }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setResults([{ email: delegateEmail, status: 'success', message: data.message }]);
      setDelegateEmail('');
      await refreshAcl(lookupEmail);
    } catch (err: any) {
      setResults([{ email: delegateEmail, status: 'error', message: err.message }]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header title="Calendar Delegation" subtitle="Share calendar access for Young Life mailboxes" />
      <Content>
        <RequirePermission permission={gmailDelegationPermission} errorPage={<AccessDeniedPanel />}>
          <BackToGam />
          <LookupForm label="Calendar Owner" helperText="Look up current calendar sharing for this user"
            value={userEmail} onChange={setUserEmail} onSubmit={handleLookup} loading={loadingList} />

          {listError && <Alert severity="error" style={{ marginTop: 16 }}>{listError}</Alert>}

          {acl !== null && (
            <div style={{ marginTop: 16 }}>
              <InfoCard title={`Calendar Sharing for ${lookupEmail}`}
                subheader={acl.length === 0 ? 'Not shared with anyone' : `Shared with ${acl.length} person${acl.length !== 1 ? 's' : ''}`}>
                {acl.length === 0
                  ? <Typography color="textSecondary">This calendar is not shared with anyone.</Typography>
                  : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Person</strong></TableCell>
                          <TableCell><strong>Access Level</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {acl.map(entry => (
                          <TableRow key={entry.delegate_email}>
                            <TableCell>{entry.delegate_email}</TableCell>
                            <TableCell>
                              <Chip label={entry.role} size="small"
                                color={entry.role === 'writer' || entry.role === 'owner' ? 'primary' : 'default'} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}

                <Divider style={{ margin: '16px 0' }} />
                <Typography variant="subtitle2" gutterBottom>Modify Calendar Sharing</Typography>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Person's Email" helperText="Who to add or remove"
                        value={delegateEmail} onChange={e => setDelegateEmail(e.target.value)}
                        required fullWidth type="email" placeholder="jane.doe@younglife.org"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField label="Action" select value={calAction}
                        onChange={e => setCalAction(e.target.value)} fullWidth>
                        <MenuItem value="calendar_add">Add Access</MenuItem>
                        <MenuItem value="calendar_remove">Remove Access</MenuItem>
                      </TextField>
                    </Grid>
                    {calAction === 'calendar_add' && (
                      <Grid item xs={12} md={3}>
                        <TextField label="Access Level" select value={role}
                          onChange={e => setRole(e.target.value)} fullWidth>
                          {CALENDAR_ROLES.map(r => (
                            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    <Grid item>
                      <Button type="submit" variant="contained" color="primary" disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={18} /> : undefined}>
                        {submitting ? 'Saving...' : calAction === 'calendar_add' ? 'Add Access' : 'Remove Access'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>

                {results.length > 0 && (
                  <Box mt={2}>
                    {results.map((r, i) => (
                      <Alert key={i} severity={r.status === 'success' ? 'success' : 'error'}>
                        <strong>{r.email}</strong> — {r.message}
                      </Alert>
                    ))}
                  </Box>
                )}
              </InfoCard>
            </div>
          )}
        </RequirePermission>
      </Content>
    </Page>
  );
}

// --- User Profile ---

type UserProfile = {
  full_name: string;
  primary_email: string;
  suspended: boolean;
  suspension_reason: string | null;
  org_unit: string;
  last_login: string | null;
  is_admin: boolean;
  is_delegated_admin: boolean;
  account_created: string | null;
  aliases: string[];
  non_editable_aliases: string[];
};

function UserProfilePage() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setProfile(null);
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('conduit-gmail-delegation');
      const res = await fetchApi.fetch(`${baseUrl}/user?user_email=${encodeURIComponent(email)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setProfile(data.user);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Page themeId="tool">
      <Header title="User Profile" subtitle="Look up account details for any Young Life user" />
      <Content>
        <RequirePermission permission={gmailDelegationPermission} errorPage={<AccessDeniedPanel />}>
          <BackToGam />
          <LookupForm label="User Email" helperText="Look up this user's account details"
            value={email} onChange={setEmail} onSubmit={handleLookup} loading={loading} />

          {error && <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>}

          {profile && (
            <div style={{ marginTop: 16 }}>
              <InfoCard
                title={profile.full_name || profile.primary_email}
                subheader={profile.primary_email}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Chip
                        label={profile.suspended ? 'Suspended' : 'Active'}
                        color={profile.suspended ? 'default' : 'primary'}
                        style={profile.suspended ? { backgroundColor: '#f44336', color: '#fff' } : undefined}
                      />
                      {profile.is_admin && <Chip label="Super Admin" size="small" />}
                      {profile.is_delegated_admin && <Chip label="Delegated Admin" size="small" />}
                    </Box>
                    {profile.suspended && profile.suspension_reason && (
                      <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                        Suspension reason: {profile.suspension_reason}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Org Unit</Typography>
                    <Typography variant="body1">{profile.org_unit || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Last Login</Typography>
                    <Typography variant="body1">{formatDate(profile.last_login)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Account Created</Typography>
                    <Typography variant="body1">{formatDate(profile.account_created)}</Typography>
                  </Grid>

                  {(profile.aliases.length > 0 || profile.non_editable_aliases.length > 0) && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                        <Typography variant="subtitle2" style={{ marginTop: 8 }}>Email Addresses</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Address</strong></TableCell>
                              <TableCell><strong>Type</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>{profile.primary_email}</TableCell>
                              <TableCell><Chip label="Primary" size="small" color="primary" /></TableCell>
                            </TableRow>
                            {profile.aliases.map(a => (
                              <TableRow key={a}>
                                <TableCell>{a}</TableCell>
                                <TableCell><Chip label="Alias" size="small" /></TableCell>
                              </TableRow>
                            ))}
                            {profile.non_editable_aliases.map(a => (
                              <TableRow key={a}>
                                <TableCell>{a}</TableCell>
                                <TableCell><Chip label="System Alias" size="small" variant="outlined" /></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Grid>
                    </>
                  )}
                </Grid>
              </InfoCard>
            </div>
          )}
        </RequirePermission>
      </Content>
    </Page>
  );
}

// --- Plugin ---

export default createFrontendPlugin({
  pluginId: 'conduit-gmail-delegation',
  extensions: [
    PageBlueprint.make({
      name: 'hub',
      params: { path: '/gam', title: 'GAM', icon: <EmailIcon />, loader: async () => <GamHub /> },
    }),
    PageBlueprint.make({
      name: 'manage',
      params: { path: '/gam/manage-delegates', title: 'Manage Gmail Delegates', icon: <EmailIcon />, loader: async () => <ManageDelegatesPage /> },
    }),
    PageBlueprint.make({
      name: 'view',
      params: { path: '/gam/view-delegates', title: 'View Gmail Delegates', icon: <PeopleIcon />, loader: async () => <ViewDelegatesPage /> },
    }),
    PageBlueprint.make({
      name: 'aliases',
      params: { path: '/gam/manage-aliases', title: 'Manage Aliases', icon: <AlternateEmailIcon />, loader: async () => <ManageAliasesPage /> },
    }),
    PageBlueprint.make({
      name: 'calendar',
      params: { path: '/gam/calendar-delegation', title: 'Calendar Delegation', icon: <EventIcon />, loader: async () => <CalendarDelegationPage /> },
    }),
    PageBlueprint.make({
      name: 'user-profile',
      params: { path: '/gam/user-profile', title: 'User Profile', icon: <PersonIcon />, loader: async () => <UserProfilePage /> },
    }),
  ],
});
